import Kanban from 'components/app/kanban/Kanban';
import AllTicketsHeader from 'components/app/support-desk/tickets-layout/AllTicketsHeader';
import CardLayout from 'components/app/support-desk/tickets-layout/CardLayout';
import TableLayout from 'components/app/support-desk/tickets-layout/TableLayout';
import TicketFilteringForm from 'components/app/support-desk/tickets-layout/TicketFilteringForm';
import EmptyList from 'components/common/EmptyList';
import { FilterContext } from 'context/Context';
import { useBreakpoints } from 'hooks/useBreakpoints';
import useBulkSelect from 'hooks/useBulkSelect';
import { useContext, useEffect, useState } from 'react';
import { Card, Col, Offcanvas, Row } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import TicketsService from 'services/Tickets.service';

const ListTickets = () => {
  const [isLoading, setIsLoading] = useState(false); // Inicialmente não carregando
  const [tickets, setTickets] = useState({ content: [] });
  const { filters, setFilters } = useContext(FilterContext);

  const [showOptionsFilter, setShowOptionsFilter] = useState(false);
  const [show, setShow] = useState(false);
  const [typeView, setTypeView] = useState(null); // Mudança para null para diferenciar do estado carregado
  const [typeViewLoaded, setTypeViewLoaded] = useState(false); // Flag para controlar se o typeView já foi carregado
  const [toggleViewFilter, setToggleViewFilter] = useState(false);
  const { selectedItems, isSelectedItem, toggleSelectedItem } = useBulkSelect(
    tickets.content.map(item => item.id)
  );
  const { breakpoints } = useBreakpoints();

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  useEffect(() => {
    // Só busca tickets quando mudam os filtros e o typeView já foi carregado e não é Kanban
    if (typeViewLoaded && typeView !== 2) {
      getTickets();
    }
  }, [filters, typeView, typeViewLoaded]);

  useEffect(() => {
    const isTypeView = localStorage.getItem('typeView');
    if (isTypeView !== null) {
      const parsedTypeView = parseInt(isTypeView);
      setTypeView(parsedTypeView);

      // Se não for Kanban, prepara para carregar
      if (parsedTypeView !== 2) {
        setIsLoading(true);
      }
    } else {
      setTypeView(0);
      setIsLoading(true);
    }

    setTypeViewLoaded(true);
  }, []);

  useEffect(() => {
    if (typeView !== null) {
      localStorage.setItem('typeView', typeView);

      if (typeView === 2) {
        setTickets({ content: [] });
        setIsLoading(false);
      } else if (typeViewLoaded) {
        getTickets();
      }
    }
  }, [typeView]);

  const getTickets = async () => {
    // Verificações de segurança para não buscar tickets em casos desnecessários
    // 1. Se typeView não foi carregado ainda
    // 2. Se estamos na visualização Kanban
    if (!typeViewLoaded || typeView === 2) {
      return;
    }

    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const response = await TicketsService.list(queryParams);
      if (response.content) {
        setTickets(response);
      }
    } catch (error) {
      console.error('Erro ao buscar tickets:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetFilters = (filterName, value) => {
    setFilters(prevState => ({ ...prevState, [filterName]: value }));
  };

  const clearFilters = () => {
    setFilters({
      page: 0,
      size: 20,
      code: '',
      priorities: '',
      status: '',
      customer: '',
      serviceCatalog: '',
      category: '',
      subcategory: '',
      subject: '',
      agent: '',
      typeTicket: '',
      initialCreationDate: '',
      endCreationDate: '',
      initialSolutionDate: '',
      endSolutionDate: '',
      statusSlaResponse: '',
      statusSlaSolution: '',
      createdBy: '',
      defaultFilter: '',
      contact: ''
    });
  };

  const isEmpty = !isLoading && tickets.content.length === 0;

  return (
    <>
      <Row className="gx-3">
        <Col
          xxl={toggleViewFilter || showOptionsFilter ? 9 : 12}
          xl={toggleViewFilter || showOptionsFilter ? 9 : 12}
          sm={toggleViewFilter || (showOptionsFilter && 12)}
        >
          <Card>
            <Card.Header className="border-bottom border-200 px-0">
              <AllTicketsHeader
                table
                layout="table-view"
                handleShow={handleShow}
                toggleViewFilter={toggleViewFilter}
                setToggleViewFilter={setToggleViewFilter}
                globalFilter={filters.subject}
                handleSetFilters={handleSetFilters}
                clearFilters={clearFilters}
                typeView={typeView}
                setTypeView={setTypeView}
                showPreMadeFilters={showOptionsFilter}
                setShowPreMadeFilters={setShowOptionsFilter}
              />
            </Card.Header>
            <Card.Body className="p-0">
              {typeView === 0 ? (
                isLoading ? (
                  <Skeleton count={10} height={44} baseColor="#5E6E82" />
                ) : isEmpty ? (
                  <EmptyList imgSize={500} vhSize={83} />
                ) : (
                  <TableLayout
                    tickets={tickets}
                    filters={filters}
                    setFilters={setFilters}
                  />
                )
              ) : typeView === 1 ? (
                isEmpty ? (
                  <EmptyList imgSize={500} vhSize={83} />
                ) : (
                  <CardLayout
                    tickets={tickets}
                    filters={filters}
                    setFilters={setFilters}
                    isSelectedItem={isSelectedItem}
                    toggleSelectedItem={toggleSelectedItem}
                  />
                )
              ) : typeView === 2 ? (
                <Kanban filters={filters} />
              ) : null}
            </Card.Body>
          </Card>
        </Col>
        {toggleViewFilter && (
          <Col xxl={3} xl={3} sm={12}>
            {breakpoints.down('xl') ? (
              <Offcanvas
                show={show}
                onHide={handleClose}
                placement="end"
                className="dark__bg-card-dark"
              >
                <Offcanvas.Header closeButton className="bg-light">
                  <h6 className="fs-0 mb-0 fw-semi-bold">Filter</h6>
                </Offcanvas.Header>
                <TicketFilteringForm
                  filters={filters}
                  setFilters={setFilters}
                  clearFilters={clearFilters}
                />
              </Offcanvas>
            ) : (
              <TicketFilteringForm
                filters={filters}
                setFilters={setFilters}
                clearFilters={clearFilters}
              />
            )}
          </Col>
        )}
      </Row>
    </>
  );
};

export default ListTickets;
